import { FbConversation, FBMessage } from '@/types/fb-type';
import React from 'react';
import { AttachmentView } from './AttachmentView';

// Assuming you have these types defined elsewhere, or use 'any' if necessary
interface ChatBubbleProps {
    message: FBMessage;
    currentConversation: FbConversation;
}

const ExpandableText = ({ text, isCustomer }: { text: string; isCustomer: boolean }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [showReplyPrompt, setShowReplyPrompt] = React.useState(false);
    const maxLength = 250;
    const shouldTruncate = text.length > maxLength;

    // Detect auto-reply format: "You are responding to a user comment to a post on your Page. View comment.(https://...)"
    const autoReplyPattern = /^(You are responding to a user comment to a post on your Page\.\s*View comment\.)\s*\((https?:\/\/[^\)]+)\)(.*)$/i;
    const match = text.match(autoReplyPattern);

    if (match) {
        const [, prefix, url, remainingText] = match;

        return (
            <div className={`text-sm leading-relaxed ${isCustomer ? 'text-white' : 'text-white'}`}>
                {/* Auto-reply notification */}
                <div className={`mb-2 p-2 rounded-md ${isCustomer ? 'bg-blue-200 border border-blue-200' : 'bg-white/10 border border-white/20'}`}>
                    <p className={`text-xs font-medium mb-1 ${isCustomer ? 'text-blue-800' : 'text-white/90'}`}>
                        📢 Auto-Reply to Comment
                    </p>
                    <p className={`text-xs ${isCustomer ? 'text-gray-700' : 'text-white/80'}`}>
                        {prefix}
                    </p>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-1 mt-1 text-xs font-medium hover:underline ${isCustomer ? 'text-blue-600' : 'text-white'
                            }`}
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        View Original Comment
                    </a>
                </div>

                {/* Remaining message text if any */}
                {remainingText && remainingText.trim() && (
                    <div className="whitespace-pre-wrap break-all">
                        {remainingText.trim()}
                    </div>
                )}

                {/* Action button to prompt user */}
                {!showReplyPrompt ? (
                    <button
                        onClick={() => setShowReplyPrompt(true)}
                        className={`mt-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isCustomer
                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                            : 'bg-white text-blue-500 hover:bg-blue-50'
                            }`}
                    >
                        💬 Send Reply Prompt
                    </button>
                ) : (
                    <div className={`mt-2 p-2 rounded-md ${isCustomer ? 'bg-green-50 border border-green-200' : 'bg-white/10 border border-white/20'}`}>
                        <p className={`text-xs ${isCustomer ? 'text-green-800' : 'text-white/90'}`}>
                            ✅ Suggested reply: "Please help chat to my page now sister/brother 🙏"
                        </p>
                    </div>
                )}
            </div>
        );
    }

    // Regular message rendering (existing logic)
    const displayedText = isExpanded || !shouldTruncate ? text : text.slice(0, maxLength) + "...";

    return (
        <div className={`text-sm leading-relaxed whitespace-pre-wrap break-all ${isCustomer ? 'text-gray-900' : 'text-white'}`}>
            {displayedText}
            {shouldTruncate && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`ml-1 text-xs font-medium hover:underline focus:outline-none ${isCustomer ? 'text-blue-600' : 'text-white/90'
                        }`}
                >
                    {isExpanded ? "Show less" : "Read more"}
                </button>
            )}
        </div>
    );
};

const ChatBubbleMsg: React.FC<ChatBubbleProps> = ({ message, currentConversation }) => {
    const user = currentConversation?.getUser();
    const isCustomer = message.getSender(currentConversation.pageId, user?.id) === 'customer';

    const hasAudioAttachment = message.attachments?.some(
        (att: any) =>
            att.audio === true ||
            att.type === 'audio' ||
            att.type === 'voice' ||
            !!att.audioData ||
            /audioclip|voice[-_]/i.test(`${att.name || ''} ${att.fileUrl || att.url || ''}`) ||
            /\.(mp3|wav|webm|ogg|m4a|aac|opus)/i.test(att.fileUrl || att.url || '')
    );

    return (
        <div className={`flex items-end gap-2 mb-4 ${isCustomer ? 'justify-start' : 'justify-end'}`}>

            {isCustomer && (
                <div className="flex-shrink-0">
                    {user?.profileImageUrl ? (
                        <img
                            className="w-8 h-8 rounded-full object-cover"
                            src={user.profileImageUrl}
                            alt={user?.name || 'User'}
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                            {user?.name?.charAt(0) || '?'}
                        </div>
                    )}
                </div>
            )}

            {/* --- MESSAGE CONTENT --- */}
            <div className={`flex flex-col min-w-0 ${hasAudioAttachment ? 'w-auto max-w-[85%] sm:max-w-md' : 'max-w-[75%] sm:max-w-md'} ${!isCustomer ? 'items-end' : ''}`}>
                <div
                    className={`px-4 py-3 shadow-sm border min-w-0 overflow-hidden break-all ${
                        hasAudioAttachment ? 'w-full sm:w-auto' : ''
                    } ${isCustomer
                        ? 'bg-white rounded-2xl rounded-bl-md border-gray-100'
                        : 'bg-blue-500 rounded-2xl rounded-br-md border-transparent'
                        }`}
                >
                    {/* Text Message */}
                    {message.message && (
                        <ExpandableText text={message.message} isCustomer={isCustomer} />
                    )}

                    {/* Sticker (only render when we have a valid URL and no attachments are present) */}
                    {message.sticker &&
                        message.sticker.startsWith('http') &&
                        (!message.attachments || message.attachments.length === 0) && (
                            <img
                                src={message.sticker}
                                alt="sticker"
                                width={100}
                                height={100}
                                className="rounded-md mt-1"
                            />
                        )}

                    {/* Attachments */}
                    {message.attachments?.map((attachment: any) => (
                        <div key={attachment.id} className="mt-2">
                            <AttachmentView att={{ attachment, isCustomer }} />
                        </div>
                    ))}
                </div>

                {/* --- TIMESTAMP & STATUS --- */}
                <div className={`flex items-center gap-1 mt-1 ${isCustomer ? 'ml-1' : 'mr-1 text-right'}`}>
                    <span className="text-[10px] text-gray-500">
                        {message.getCreatedAt().toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </span>

                    {/* Status Checkmark (Staff side only) */}
                    {!isCustomer && (
                        <div className="w-3.5 h-3.5 rounded-full bg-blue-500 flex items-center justify-center">
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatBubbleMsg;
